import { useQuery } from "@tanstack/react-query";
import { HISTORY_OPTION_LABELS } from "@amber/profiles/contracts";
import { AppLink, Tag, formatDate, Overline } from "@amber/ui";
import { personPlanQuery, sourceOptionsQuery } from "../queries";

export function PlanSummary({ profileId }: { profileId: string }) {
  const plan = useQuery(personPlanQuery(profileId)).data?.plan ?? null;
  const options = useQuery(sourceOptionsQuery).data?.sources ?? [];
  if (!plan) return null;
  const revision = plan.applied ?? plan.latest;
  const title = (id: string) => options.find((o) => o.id === id)?.title ?? "Unknown source";
  return (
    <section aria-label="Monitoring plan" className="mt-3 rounded-xl border border-divider px-4 py-3 text-ui">
      <Overline className="m-0">Monitoring plan</Overline>
      <dl className="m-0 mt-2 grid grid-cols-[140px_1fr] gap-x-3 gap-y-1.5">
        <dt className="text-muted">Sources</dt>
        <dd className="m-0">
          {revision.sources.length === 0
            ? "None of its own; shared sources cover this person"
            : revision.sources.map((s) => (
                <span key={s.source_id} className="mr-3 inline-flex items-center gap-1">
                  <AppLink href={`/sources/${s.source_id}`}>{title(s.source_id)}</AppLink>
                  {s.required ? <Tag tone="strong">Required</Tag> : null}
                </span>
              ))}
        </dd>
        <dt className="text-muted">Languages</dt>
        <dd className="m-0">{revision.languages.map((l) => l.toUpperCase()).join(", ") || "Not set"}</dd>
        <dt className="text-muted">Monitoring start</dt>
        <dd className="m-0">{formatDate(revision.monitoring_start)}</dd>
        <dt className="text-muted">History</dt>
        <dd className="m-0">
          {HISTORY_OPTION_LABELS[revision.history.option]} · back to {formatDate(revision.back_to)}{" "}
          {revision.history_custom ? <Tag tone="accent">custom</Tag> : null}
        </dd>
        <dt className="text-muted">Review date</dt>
        <dd className="m-0">{formatDate(revision.review_date)}</dd>
        <dt className="text-muted">Rationale</dt>
        <dd className="m-0">{revision.rationale}</dd>
      </dl>
      <p className="m-0 mt-2 text-md text-muted">
        {plan.applied?.applied_at
          ? `Applied ${formatDate(plan.applied.applied_at)}.${plan.pending ? " Newer changes are pending." : ""}`
          : "Waiting for the scheduler to apply it; it never claims earlier coverage."}
      </p>
    </section>
  );
}
